import { defineStore } from 'pinia'
import { ref, computed, watch, onUnmounted } from 'vue'
import { DateTime } from 'luxon'
import { LocalStorage } from 'quasar'
import { QueryParams } from 'src/models/query-parameters'
import { decodeTestModeToken } from 'src/services/JWTDecoder'

interface StoredTestModeOptions {
  freePrices: boolean
}

const TEST_MODE_OPTIONS_STORAGE_KEY = 'ml_app_test_mode_options'

export const useTestModeStore = defineStore('test-mode', () => {
  const token = ref<string | null>(null)
  const expires = ref<DateTime | null>(null)
  const now = ref(DateTime.now())
  const freePrices = ref(false)

  const timer = setInterval(() => {
    now.value = DateTime.now()
  }, 1000)

  onUnmounted(() => clearInterval(timer))

  const active = computed(
    () => expires.value !== null && now.value < expires.value,
  )

  const storedOptions = computed<StoredTestModeOptions>(() => ({
    freePrices: freePrices.value,
  }))

  const activeOptions = computed<string[]>(() => {
    const options: string[] = []
    if (freePrices.value) options.push('free-prices')
    return options
  })

  watch(storedOptions, (value) => {
    LocalStorage.set(TEST_MODE_OPTIONS_STORAGE_KEY, value)
  })

  function initialise(rawToken: null | string): void {
    if (rawToken !== null) {
      applyToken(rawToken)
    } else {
      const tokenFromStorage = LocalStorage.getItem<string>(
        QueryParams.TEST_MODE_TOKEN,
      )
      if (tokenFromStorage !== null) {
        applyToken(tokenFromStorage)
      }
    }

    const saved = LocalStorage.getItem<StoredTestModeOptions>(
      TEST_MODE_OPTIONS_STORAGE_KEY,
    )
    if (saved !== null) {
      freePrices.value = saved.freePrices ?? false
    }
  }

  function applyToken(rawToken: string): void {
    try {
      const decoded = decodeTestModeToken(rawToken)
      LocalStorage.set(QueryParams.TEST_MODE_TOKEN, rawToken)
      token.value = rawToken
      expires.value = decoded.expires
    } catch (e) {
      console.error(e)
      LocalStorage.remove(QueryParams.TEST_MODE_TOKEN)
    }
  }

  function exit(): void {
    LocalStorage.remove(QueryParams.TEST_MODE_TOKEN)
    LocalStorage.remove(TEST_MODE_OPTIONS_STORAGE_KEY)
    token.value = null
    expires.value = null
    window.location.reload()
  }

  return {
    token,
    expires,
    active,
    activeOptions,
    now,
    freePrices,
    initialise,
    exit,
  }
})
